import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from './utils';

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type DialogVariant = 'default' | 'lightbox';

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

/** Root — usually controlled by a parent component that owns `open`. */
export function Dialog({ open, onOpenChange, children }: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </DialogPrimitive.Root>
  );
}

export const DialogTrigger = DialogPrimitive.Trigger;

export interface DialogContentProps {
  size?: DialogSize;
  /**
   * `default` renders the standard white card. `lightbox` renders no chrome
   * (no bg/rounded/shadow/padding) and uses an opaque dark overlay — for
   * image viewers and similar full-bleed content.
   */
  variant?: DialogVariant;
  children: ReactNode;
  className?: string;
  /** Forwarded to Radix — call `event.preventDefault()` to keep the dialog open. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /** Forwarded to Radix — fires on backdrop click / outside interaction. */
  onInteractOutside?: DialogPrimitive.DialogContentProps['onInteractOutside'];
  /** Forwarded to Radix — call `event.preventDefault()` to skip default auto-focus. */
  onOpenAutoFocus?: DialogPrimitive.DialogContentProps['onOpenAutoFocus'];
  /**
   * Forwarded to Radix. Pass `undefined` to opt out of Radix's accessibility
   * warning when the dialog has no `<DialogDescription>`.
   */
  'aria-describedby'?: string;
}

const sizeStyles: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
  '2xl': 'max-w-3xl',
};

/**
 * Structural shell for a dialog. Compose with `DialogHeader`, `DialogBody`,
 * and `DialogFooter` — it owns only positioning, sizing, and scroll.
 */
export function DialogContent(props: DialogContentProps) {
  const {
    size = 'md',
    variant = 'default',
    children,
    className = '',
    onEscapeKeyDown,
    onInteractOutside,
    onOpenAutoFocus,
  } = props;
  const isLightbox = variant === 'lightbox';
  const cls = cn(
    'fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
    // dvh, not vh: on a phone 100vh is the screen with the browser's toolbars
    // hidden, so a tall dialog centred in the visible area put its footer (and
    // the Save button in it) behind the toolbar, out of reach of any scroll.
    'w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)]',
    !isLightbox && 'bg-white rounded-surface shadow-dialog flex flex-col overflow-hidden',
    'focus:outline-none',
    sizeStyles[size],
    className,
  );
  const overlayCls = isLightbox
    ? 'fixed inset-0 z-40 bg-black/90 print:hidden data-[state=open]:animate-in data-[state=closed]:animate-out'
    : 'fixed inset-0 z-40 bg-black/50 print:hidden data-[state=open]:animate-in data-[state=closed]:animate-out';

  const ariaProps: { 'aria-describedby'?: string } = {};
  if ('aria-describedby' in props) {
    ariaProps['aria-describedby'] = props['aria-describedby'];
  }

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlayCls} />
      <DialogPrimitive.Content
        className={`z-50 print:hidden ${cls}`}
        onEscapeKeyDown={onEscapeKeyDown}
        onInteractOutside={onInteractOutside}
        onOpenAutoFocus={onOpenAutoFocus}
        {...ariaProps}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export interface DialogHeaderProps {
  children: ReactNode;
  className?: string;
  /** Hide the built-in close button. Use when the header composes its own. */
  hideCloseButton?: boolean;
}

/** Top region of a dialog — padded, bordered, aligns close button to the right. */
export function DialogHeader({ children, className = '', hideCloseButton = false }: DialogHeaderProps) {
  const cls = cn(
    'flex items-start justify-between gap-4',
    'px-6 pt-5 pb-4 border-b border-neutral-200',
    className,
  );
  return (
    <div className={cls}>
      <div className="min-w-0 flex-1">{children}</div>
      {!hideCloseButton && (
        <DialogPrimitive.Close
          className="-m-1 p-1 text-neutral-500 hover:text-neutral-800 transition-colors focus:outline-none focus:ring-2 focus:ring-accent rounded"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

export interface DialogTitleProps {
  children: ReactNode;
  className?: string;
}

export function DialogTitle({ children, className = '' }: DialogTitleProps) {
  const cls = cn('font-heading text-xl font-semibold text-primary', className);
  return <DialogPrimitive.Title className={cls}>{children}</DialogPrimitive.Title>;
}

export interface DialogDescriptionProps {
  children: ReactNode;
  className?: string;
}

export function DialogDescription({ children, className = '' }: DialogDescriptionProps) {
  const cls = cn('text-sm text-neutral-600 mt-1', className);
  return <DialogPrimitive.Description className={cls}>{children}</DialogPrimitive.Description>;
}

export interface DialogBodyProps {
  children: ReactNode;
  className?: string;
}

/** Main scrollable content region. Absorbs vertical overflow. */
export function DialogBody({ children, className = '' }: DialogBodyProps) {
  const cls = cn('flex-1 overflow-y-auto px-6 py-4 min-h-0', className);
  return <div className={cls}>{children}</div>;
}

export interface DialogFooterProps {
  children: ReactNode;
  className?: string;
}

export function DialogFooter({ children, className = '' }: DialogFooterProps) {
  const cls = cn(
    'flex items-center justify-end gap-2',
    'px-6 py-4 border-t border-neutral-200',
    className,
  );
  return <div className={cls}>{children}</div>;
}

export const DialogClose = DialogPrimitive.Close;
